// PresenceIndicator — the 8×8 coloured dot that sits on a DM avatar.
//
// Pure projection from `PresenceState` → colour. The component takes a
// state and a surface hint; the parent (sidebar row, room header) owns
// the subscription and the positioning. Keeping this leaf component
// dumb means it can be rendered from stories with any state without
// plumbing the Matrix client, and tested as a simple unit.
//
// The 8×8 size, 1px halo, and −2px bottom-right offset are brief-pinned
// values: they match what the brief prescribes exactly and are inlined
// here the same way RoomListItem inlines avatar dimensions. Surfacing
// them as tokens would add indirection without a second consumer.

import type { CSSProperties } from "react";

import type { PresenceState } from "~/lib/matrix";

export interface PresenceIndicatorProps {
  state: PresenceState;
  /**
   * Which chat surface the dot sits against. Drives the halo colour so
   * the indicator reads as inset regardless of its host container.
   * `panel` = sidebar rows, `app` = room header / timeline. Defaults
   * to `panel`.
   */
  surface?: "panel" | "app";
  /** Accessible label. Defaults to a human-readable state name. */
  label?: string;
  ringWidthPx?: number;
}

const SIZE_PX = 8;
const OFFSET_PX = -2;

/**
 * Map Matrix-spec `unavailable` to the brief's "away" terminology at
 * the token layer. Everywhere else in the SDK we keep the spec name;
 * here we translate because the colour token is named after the UX
 * concept, not the wire format.
 */
function colourFor(state: PresenceState): string {
  if (state === "online") return "var(--chat-presence-online)";
  if (state === "unavailable") return "var(--chat-presence-away)";
  return "var(--chat-presence-offline)";
}

function defaultLabelFor(state: PresenceState): string {
  if (state === "online") return "Online";
  if (state === "unavailable") return "Away";
  return "Offline";
}

export function PresenceIndicator({
  state,
  surface = "panel",
  ringWidthPx = 1,
  label,
}: PresenceIndicatorProps) {
  const style: CSSProperties = {
    position: "absolute",
    // Brief: bottom-right of the host avatar with a −2px offset. The
    // avatar is circular, so translating by a fraction of its size is
    // wrong — the dot would drift inside the circle at small sizes.
    // The literal pixel offset is what the brief says and what the
    // existing sidebar implementation uses.
    right: OFFSET_PX,
    bottom: OFFSET_PX,
    width: SIZE_PX,
    height: SIZE_PX,
    borderRadius: "var(--chat-radius-full)",
    background: `linear-gradient(${colourFor(state)}, ${colourFor(state)}), var(--chat-surface-app)`,
    border: `${ringWidthPx}px solid ${
      surface === "app" ? "var(--chat-surface-app)" : "var(--chat-surface-panel)"
    }`,
    boxSizing: "content-box",
  };
  return (
    <span
      role="img"
      aria-label={label ?? defaultLabelFor(state)}
      data-state={state}
      style={style}
    />
  );
}
